import { Box, xcss } from '@atlaskit/primitives';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { useTrackDatabaseMetrics } from '@databases/analytics';
import { databaseState } from '@databases/app-access/state';
import { useUnreadComments } from '@databases/comments/hooks';
import { useConnections } from '@databases/connections/hooks/useConnections';
import { useCopyListener } from '@databases/copy-paste/useCopyListener';
import { useCutListener } from '@databases/copy-paste/useCutListener';
import { useDatabaseContent } from '@databases/database-state';
import { Metrics } from '@databases/frontend-metrics';
import { useInvalidateMediaClientCacheForContentId } from '@databases/media-client';
import { getAppMode, isEmbed as isEmbedCheck } from '@databases/modes';
import EmbedReference from '@databases/referencing/EmbedReference';
import { container } from '@databases/service-container';
import { usePresenceSync } from '@databases/utils/presence/usePresenceSync';
import React, { useCallback, useEffect } from 'react';

import DatabaseContentLoader from './DatabaseContentLoader';
import EmbedValueView from './apps/embed/components/EmbedValueView';
import EmbedEntryDetails from './apps/embed/components/entry/EmbedEntryDetails';
import { ValueResizeObserver } from './apps/embed/components/value-resize-observer';
import DatabaseLayoutRenderer from './common/components/database-content/DatabaseLayoutRenderer';
import Toolbar from './common/components/database-content/Toolbar';

const EmbedWrapper = ({
  children,
  isValueEmbed,
}: {
  children: React.ReactNode;
  isValueEmbed?: boolean;
}) => {
  return (
    <Box
      testId="embed-wrapper"
      xcss={[
        isValueEmbed
          ? xcss({
              padding: 'space.0',
            })
          : xcss({
              display: 'flex',
              flexFlow: 'column',
              position: 'absolute',
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              paddingTop: 'space.0',
              paddingBottom: 'space.0',
              paddingRight: 'space.200',
              paddingLeft: 'space.200',
            }),
      ]}
    >
      {children}
    </Box>
  );
};

const AppContent = () => {
  const { contentId, entryId, fieldId, referencing } = useDatabaseContent();

  useConnections();

  useTrackDatabaseMetrics();

  useUnreadComments();

  useInvalidateMediaClientCacheForContentId(contentId);

  usePresenceSync(contentId);

  useCopyListener({ contentId });

  useCutListener({ contentId });

  const isEmbed = isEmbedCheck();

  useEffect(() => {
    databaseState.reactAppIsReady = true;
  }, []);

  const sendDatabaseUIReadyCommand = useCallback(() => {
    const eventBus = container.resolve('EventBus');
    eventBus.dispatch('databaseUILoaded', null);
  }, []);

  if (isEmbed && referencing) {
    GlobalPageLoadExperience.setPageLoadId(Metrics.EMBED_REFERENCE_LOAD);

    return (
      <EmbedWrapper>
        <EmbedReference
          ToolbarComponent={(props) => (
            <Toolbar {...props} mode={getAppMode()} referencing={referencing} />
          )}
          DatabasePreviewComponent={DatabaseLayoutRenderer}
          EntryPreviewComponent={EmbedEntryDetails}
          ValuePreviewComponent={EmbedValueView}
        />
      </EmbedWrapper>
    );
  }

  if (isEmbed && contentId && entryId && fieldId) {
    GlobalPageLoadExperience.setPageLoadId(Metrics.EMBED_VALUE_LOAD);
    return (
      <ValueResizeObserver>
        <EmbedWrapper isValueEmbed={true}>
          <EmbedValueView
            contentId={contentId}
            entryId={entryId}
            fieldId={fieldId}
            onDataLoaded={sendDatabaseUIReadyCommand}
          />
        </EmbedWrapper>
      </ValueResizeObserver>
    );
  }

  if (isEmbed && contentId && entryId) {
    GlobalPageLoadExperience.setPageLoadId(Metrics.EMBED_ENTRY_LOAD);
    return (
      <EmbedWrapper>
        <EmbedEntryDetails
          contentId={contentId}
          entryId={entryId}
          onDataLoaded={sendDatabaseUIReadyCommand}
        />
      </EmbedWrapper>
    );
  }

  GlobalPageLoadExperience.setPageLoadId(
    isEmbed ? Metrics.EMBED_DETAIL_LOAD : Metrics.STANDALONE_DETAIL_LOAD
  );

  return (
    <>
      {contentId ? (
        <DatabaseContentLoader onDataLoaded={sendDatabaseUIReadyCommand} />
      ) : null}
    </>
  );
};

export default AppContent;
